import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, X, FileText } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Travel Tips', 'Destination Guide', 'Packing List', 'Budget Travel', 'Luxury Travel', 'Adventure', 'Culture', 'Food & Cuisine'];

const emptyPost = { title: '', excerpt: '', content: '', cover_image: '', category: 'Travel Tips', tags: [], author: 'Dnotours Team', is_published: false, meta_description: '' };

export default function AdminBlog() {
  const { token } = useAdmin();
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(emptyPost);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tagsInput, setTagsInput] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/blog`, { headers });
    setPosts(data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.content) return toast.error('Title and content required');
    const payload = { ...form, tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean) };
    try {
      if (editing) {
        await axios.put(`${API}/admin/blog/${editing}`, payload, { headers });
        toast.success('Post updated');
      } else {
        await axios.post(`${API}/admin/blog`, payload, { headers });
        toast.success('Post created');
      }
      setShowForm(false); setEditing(null); setForm(emptyPost); setTagsInput(''); load();
    } catch (e) { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await axios.delete(`${API}/admin/blog/${id}`, { headers });
    toast.success('Deleted'); load();
  };

  const edit = async (p) => {
    const { data } = await axios.get(`${API}/admin/blog/${p.id}`, { headers });
    setForm(data);
    setTagsInput((data.tags || []).join(', '));
    setEditing(p.id);
    setShowForm(true);
  };

  const togglePublish = async (p) => {
    await axios.put(`${API}/admin/blog/${p.id}`, { is_published: !p.is_published }, { headers });
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-blog">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Blog Manager</h1>
          <p className="text-slate-500 text-sm mt-1">Create and manage travel blog posts</p>
        </div>
        <Button onClick={() => { setForm(emptyPost); setEditing(null); setTagsInput(''); setShowForm(true); }} data-testid="add-blog-btn">
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <Card className="w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editing ? 'Edit' : 'Create'} Blog Post</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-title" /></div>
              <div><Label>Excerpt (short summary)</Label><Textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} data-testid="blog-excerpt" /></div>
              <div><Label>Content (HTML supported)</Label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={12} className="font-mono text-sm" data-testid="blog-content" /></div>
              <div><Label>Cover Image URL</Label><Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://images.unsplash.com/..." data-testid="blog-cover" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <select className="w-full border rounded-md px-3 py-2 text-sm mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="blog-category">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div><Label>Author</Label><Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} /></div>
              </div>
              <div><Label>Tags (comma-separated)</Label><Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="travel, tips, budget" data-testid="blog-tags" /></div>
              <div><Label>Meta Description (SEO)</Label><Textarea value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} rows={2} /></div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
                <Label>Published</Label>
              </div>
              <Button className="w-full" onClick={save} data-testid="save-blog-btn">{editing ? 'Update' : 'Create'} Post</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-slate-800">{posts.length}</p><p className="text-xs text-slate-500">Total Posts</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-green-600">{posts.filter(p => p.is_published).length}</p><p className="text-xs text-slate-500">Published</p></CardContent></Card>
        <Card><CardContent className="py-4 text-center"><p className="text-2xl font-bold text-amber-600">{posts.filter(p => !p.is_published).length}</p><p className="text-xs text-slate-500">Drafts</p></CardContent></Card>
      </div>

      {/* List */}
      <div className="space-y-3">
        {posts.length === 0 && <p className="text-slate-500 text-center py-8">No blog posts yet. Write your first article!</p>}
        {posts.map((p) => (
          <Card key={p.id} data-testid={`blog-card-${p.id}`}>
            <CardContent className="py-4 flex items-center gap-4">
              {p.cover_image ? (
                <img src={p.cover_image} alt={p.title} className="w-20 h-14 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-20 h-14 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0"><FileText className="w-6 h-6 text-slate-400" /></div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 truncate">{p.title}</p>
                <p className="text-xs text-slate-500">{p.category} • {p.author} • {p.views || 0} views</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs px-2 py-1 rounded ${p.is_published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {p.is_published ? 'Published' : 'Draft'}
                </span>
                <Switch checked={p.is_published} onCheckedChange={() => togglePublish(p)} />
                <Button variant="ghost" size="icon" onClick={() => edit(p)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(p.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
